import { useState, useMemo, useEffect } from 'react'
import {
	TextField,
	Button,
	Container,
	Box,
	Typography,
	Divider,
	useTheme,
	useMediaQuery,
	Dialog,
	DialogContent,
	Paper,
	Alert,
	AlertTitle,
}                                    from '@mui/material'
import { EmailOutlined }             from '@mui/icons-material'
import { getSupabase }               from "../../util/supabase-utils"
import Google                        from "../icons/Google"
import BirdsyMiniLogo                from "../BirdsyMiniLogo"
import { doGet, doPost }             from "../../util/do-fetch"
import { useLocation, useNavigate }  from "react-router-dom"

const supabase = getSupabase()

/*
 * Local-only sign-in (087): under `npm start`, the email field signs you straight in instead of
 * mailing a link. The API is the real gate -- /mini/dev-login 404s unless DEV_LOGIN=true and it
 * is pointed at a local Supabase -- so this flag only decides whether the UI offers it.
 */
const DEV_LOGIN = process.env.NODE_ENV === 'development'

const AuthIntro = () => {
	const theme = useTheme()
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
	const location = useLocation()
	const navigate = useNavigate()

	const queryParams = new URLSearchParams(location.search)
	const serverError = useMemo(() => queryParams.get('error') === 'server_error', [location.search])
	const errorDescription = queryParams.get('error_description')
	const next = queryParams.get('next') || '/prepkit'
	const msgToken = queryParams.get('msg')

	const redirectTo = `${process.env.REACT_APP_SITE_URL}/auth/callback?next=${encodeURIComponent(next)}`

	const [email, setEmail]               = useState('')
	const [sending, setSending]           = useState(false)
	const [sent, setSent]                 = useState(false)
	const [signedIn, setSignedIn]         = useState(false)
	const [magicLinkError, setMagicLinkError] = useState(null)
	/*
	 * The built-in copy, rendered immediately so the screen is never blank while the fetch is in
	 * flight. The API resolves ?msg=<TOKEN> -> DEFAULT -> the same strings (085), so in the
	 * common case the response matches what is already on screen and nothing visibly changes.
	 */
	const [welcome, setWelcome] = useState({
		title: 'Welcome to Birdsy',
		subtitle: 'Sign in or create an account to continue.',
	})

	useEffect(() => {
		let cancelled = false
		/*
		 * `next` travels with the request so the API can match the message to where the link is
		 * going -- /login?next=/try/... picks up the message whose destination path is /try, with
		 * no ?msg= on the link at all (092). An explicit ?msg= still wins.
		 */
		const query = next ? `?next=${encodeURIComponent(next)}` : ''
		const path = msgToken
			? `/mini/login-message/${encodeURIComponent(msgToken)}${query}`
			: `/mini/login-message${query}`

		doGet(path)
			.then(response => response.ok ? response.json() : null)
			.then(data => {
				if (!cancelled && data && data.title) {
					setWelcome({title: data.title, subtitle: data.subtitle})
				}
			})
			// a welcome message must never get in the way of signing in
			.catch(err => console.debug('Could not load the login message', msgToken, err))

		return () => { cancelled = true }
	}, [msgToken, next])

	useEffect(() => {
		if (!sent) return
		const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
			if (event === 'SIGNED_IN' && session) {
				setSignedIn(true)
			}
		})
		return () => subscription.unsubscribe()
	}, [sent])

	const handleMagicLink = async () => {
		if (!email) return
		setSending(true)
		setMagicLinkError(null)
		const { error } = await supabase.auth.signInWithOtp({
			email,
			options: { emailRedirectTo: redirectTo }
		})
		setSending(false)
		if (error) {
			setMagicLinkError(error.message)
		} else {
			setSent(true)
		}
	}

	/*
	 * The API mints and redeems a magic link for any address and hands back the resulting
	 * session, which setSession installs. The session is a genuine one from GoTrue, so the
	 * store's SIGNED_IN listener and the JWT the API verifies behave exactly as they do after
	 * a real emailed link -- claims from the access token hook included.
	 */
	const handleDevLogin = async () => {
		if (!email) return
		setSending(true)
		setMagicLinkError(null)
		try {
			const response = await doPost('/mini/dev-login', { body: JSON.stringify({ email }) })
			if (!response.ok) {
				setMagicLinkError('Dev sign-in is not enabled on this API (needs DEV_LOGIN=true and a local Supabase).')
				return
			}
			const { access_token, refresh_token } = await response.json()
			const { error } = await supabase.auth.setSession({ access_token, refresh_token })
			if (error) setMagicLinkError(error.message)
			else navigate(next)
		} catch (err) {
			setMagicLinkError('Dev sign-in failed - is the API running?')
		} finally {
			setSending(false)
		}
	}

	const handleGoogleLogin = async () => {
		const { error } = await supabase.auth.signInWithOAuth({
			provider: 'google',
			options: { redirectTo }
		})
		if (error) console.error('Google login error:', error.message)
	}

	const content = signedIn ? (
		<Box textAlign="center" py={2}>
			<Typography variant="h6">You're signed in!</Typography>
			<Typography variant="body2" color="text.secondary" mt={1}>
				You've successfully signed in.
			</Typography>
			<Button variant="contained" sx={{ mt: 3 }} onClick={() => navigate(next)}>
				Continue
			</Button>
		</Box>
	) : sent ? (
		<Box textAlign="center" py={2}>
			<Typography variant="h6">Check your email</Typography>
			<Typography variant="body2" color="text.secondary" mt={1}>
				We sent a sign-in link to <strong>{email}</strong>.<br />
				Click the link in your email to continue.
			</Typography>
			<Button sx={{ mt: 3 }} onClick={() => setSent(false)}>
				Use a different email
			</Button>
		</Box>
	) : (
		<Box component="form" mt={0} mb={2} onSubmit={(e) => { e.preventDefault(); DEV_LOGIN ? handleDevLogin() : handleMagicLink() }}>
			{serverError && (
				<Alert severity="error" sx={{ mb: 2 }}>
					<AlertTitle>Error</AlertTitle>
					{errorDescription}
				</Alert>
			)}
			{magicLinkError && (
				<Alert severity="error" sx={{ mb: 2 }}>{magicLinkError}</Alert>
			)}

			<Typography variant="body1" sx={{ fontSize: '1.1em', mb: 4, letterSpacing: '-0.04em' }}>
				{welcome.subtitle}
			</Typography>

			<TextField
				fullWidth
				type="email"
				label="Email address"
				value={email}
				onChange={(e) => setEmail(e.target.value)}
				autoFocus
				sx={{ mb: 1 }}
			/>

			<Button
				type="submit"
				variant="contained"
				size="large"
				fullWidth
				disabled={!email || sending}
				sx={{ mt: 1, py: 2 }}
				startIcon={<EmailOutlined />}
			>
				{DEV_LOGIN
					? (sending ? 'Signing in...' : 'Sign in (local dev)')
					: (sending ? 'Sending...' : 'Continue with Email')}
			</Button>

			{/* local dev signs in on the email alone (087) - no second route in */}
			{!DEV_LOGIN && (
				<>
				<Box display="flex" flexDirection="row" alignItems="center" py={2}>
					<Box sx={{ flex: 1 }}><Divider /></Box>
					<Box sx={{ px: 2, fontSize: '.8em', color: theme.palette.text.disabled }}>or</Box>
					<Box sx={{ flex: 1 }}><Divider /></Box>
				</Box>

				<Button
					variant="outlined"
					size="large"
					fullWidth
					sx={{ py: 2 }}
					onClick={handleGoogleLogin}
				>
					<Google />
					<Box sx={{ ml: 1 }}>Continue with Google</Box>
				</Button>
				</>
			)}
		</Box>
	)

	return (
		<Container style={{ maxWidth: isSmallScreen ? '100%' : '480px', padding: isSmallScreen ? '0 16px' : '0' }}>
			{/*
			  * The Dialog's own Paper is the layout wrapper only — it still carries the transition
			  * and the focus trap, but has no surface of its own. The visible card is the inner
			  * Paper, which lets the logo sit above it on the backdrop rather than inside it.
			  */}
			<Dialog
				open={true}
				fullScreen={isSmallScreen}
				BackdropProps={{ sx: { backgroundColor: theme.palette.background.paper } }}
				/*
				 * backgroundImage:none matters as much as the colour. In dark mode MUI paints Paper
				 * elevation as a background-image gradient, and Dialog defaults to elevation 24 --
				 * so a transparent backgroundColor still left a grey slab showing behind the logo,
				 * above the real card.
				 */
				PaperProps={{ sx: { backgroundColor: 'transparent', backgroundImage: 'none', boxShadow: 'none', overflow: 'visible' } }}
			>
				<Box display="flex" flexDirection="column" alignItems="center">
					<Box sx={{ lineHeight: 0, mb: isSmallScreen ? 4 : 6 }}>
						<BirdsyMiniLogo width={isSmallScreen ? 125 : 175} />
					</Box>

					<Paper
						elevation={0}
						sx={{
							width: '100%',
							borderRadius: `${theme.custom.radius.lg}px`,
							// the design system's lifted-card shadow: wider, softer and mode-aware
							boxShadow: isSmallScreen ? 'none' : theme.custom.shadow.pop,
						}}
					>
						<DialogContent sx={{ p: 5 }}>
							{!signedIn && !sent && welcome.title && (
								<Typography variant="h5" sx={{ mb: 1, fontWeight: 700, letterSpacing: '-0.03em' }}>
									{welcome.title}
								</Typography>
							)}

							{content}
						</DialogContent>

						<Box flexDirection="row" display="flex" justifyContent="center" py={1}>
							<Button onClick={() => navigate(-1)}>&larr; Back</Button>
							<Button onClick={() => navigate('/prep')}>Home</Button>
						</Box>
					</Paper>
				</Box>
			</Dialog>
		</Container>
	)
}

export default AuthIntro
